import React, { useMemo, useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Loader2, GitMerge } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';
import CategorizedProductSelectContent from '../components/CategorizedProductSelectContent';

const FE_SITE_ORDER = ['FE Génipa', 'FE Cluny', 'FE Dillon', 'FE Océanis', 'FE Vauclin', 'FE Siège'];

export default function ConsolidatedManualReceptionDialog({ open, onOpenChange, suppliers, sites, supplierProducts, industrialProducts, onCreated }) {
    const [supplierId, setSupplierId] = useState('');
    const [receptionDate, setReceptionDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [documentNumber, setDocumentNumber] = useState('');
    const [notes, setNotes] = useState('');
    const [items, setItems] = useState([]);
    const [currentProductId, setCurrentProductId] = useState('');
    const [saving, setSaving] = useState(false);

    const feSites = useMemo(
        () => FE_SITE_ORDER.map(n => sites.find(s => s.name === n)).filter(Boolean),
        [sites]
    );
    const availableProducts = useMemo(
        () => supplierProducts.filter(sp => sp.supplier_id === supplierId && !items.some(it => it.supplier_product_id === sp.id)),
        [supplierProducts, supplierId, items]
    );

    const reset = () => {
        setSupplierId(''); setDocumentNumber(''); setNotes(''); setItems([]); setCurrentProductId('');
        setReceptionDate(new Date().toISOString().split('T')[0]);
    };

    const addProduct = () => {
        const sp = supplierProducts.find(p => p.id === currentProductId);
        if (!sp) return;
        setItems(prev => [...prev, {
            supplier_product_id: sp.id,
            industrial_product_id: sp.industrial_product_id,
            name: sp.supplier_name_product,
            ref: sp.supplier_ref || '',
            variable_weight: !!sp.variable_weight,
            unit_price: sp.unit_price != null ? String(sp.unit_price) : '',
            quantities: Object.fromEntries(feSites.map(s => [s.id, ''])),
        }]);
        setCurrentProductId('');
    };

    const setQty = (idx, siteId, value) => {
        setItems(prev => prev.map((it, i) => i === idx ? { ...it, quantities: { ...it.quantities, [siteId]: value } } : it));
    };
    const setPrice = (idx, value) => {
        setItems(prev => prev.map((it, i) => i === idx ? { ...it, unit_price: value } : it));
    };
    const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

    const lineTotalQty = (it) => Object.values(it.quantities).reduce((a, v) => a + (parseFloat(v) || 0), 0);
    const grandTotal = items.reduce((a, it) => a + lineTotalQty(it) * (parseFloat(it.unit_price) || 0), 0);

    const handleSave = async () => {
        if (!supplierId) { toast.error('Sélectionnez un fournisseur'); return; }
        if (!items.length) { toast.error('Ajoutez au moins un produit'); return; }
        if (items.some(it => lineTotalQty(it) <= 0)) { toast.error('Chaque produit doit avoir au moins une quantité répartie'); return; }
        setSaving(true);
        try {
            const payload = {
                supplier_id: supplierId,
                reception_date: receptionDate,
                document_number: documentNumber,
                notes,
                items: items.map(it => ({
                    supplier_product_id: it.supplier_product_id,
                    industrial_product_id: it.industrial_product_id,
                    unit_price: parseFloat(it.unit_price) || 0,
                    quantities: Object.fromEntries(Object.entries(it.quantities).map(([k, v]) => [k, parseFloat(v) || 0])),
                })),
            };
            const r = await api.post('/manual-receptions/consolidated', payload);
            const recs = r.data?.receptions || [];
            toast.success(`${recs.length} réception(s) créée(s) : ${recs.map(x => x.reception_number).join(', ')}`);
            reset();
            onOpenChange(false);
            onCreated?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
            <DialogContent className="w-[98vw] sm:!max-w-[1200px] max-h-[92vh] flex flex-col overflow-hidden">
                <DialogHeader className="flex-shrink-0">
                    <DialogTitle className="flex items-center gap-2">
                        <GitMerge className="w-5 h-5 text-emerald-600" />
                        Réception consolidée — ventilation 6 sites
                    </DialogTitle>
                </DialogHeader>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-shrink-0">
                    <Select value={supplierId} onValueChange={(v) => { setSupplierId(v); setItems([]); setCurrentProductId(''); }}>
                        <SelectTrigger data-testid="cons-reception-supplier-select">
                            <SelectValue placeholder="Fournisseur" />
                        </SelectTrigger>
                        <SelectContent>
                            {[...suppliers].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })).map(s => (
                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Input type="date" value={receptionDate} onChange={(e) => setReceptionDate(e.target.value)} data-testid="cons-reception-date-input" />
                    <Input placeholder="N° de pièce (BL / facture)" value={documentNumber} onChange={(e) => setDocumentNumber(e.target.value)} data-testid="cons-reception-doc-input" />
                    <Input placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="cons-reception-notes-input" />
                </div>

                {supplierId && (
                    <div className="flex items-end gap-2 border-b pb-2 flex-shrink-0">
                        <div className="flex-1 min-w-0">
                            <Select value={currentProductId} onValueChange={setCurrentProductId}>
                                <SelectTrigger className="h-8 text-xs" data-testid="cons-reception-product-select">
                                    <SelectValue placeholder="Ajouter un produit" />
                                </SelectTrigger>
                                <CategorizedProductSelectContent
                                    products={availableProducts}
                                    industrialProducts={industrialProducts}
                                />
                            </Select>
                        </div>
                        <Button type="button" onClick={addProduct} disabled={!currentProductId}
                            className="bg-emerald-500 hover:bg-emerald-600 h-8 px-3" data-testid="cons-reception-add-product-btn">
                            <Plus className="w-4 h-4" />
                        </Button>
                    </div>
                )}

                <div className="flex-1 overflow-auto min-h-0">
                    {items.length > 0 ? (
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50 sticky top-0 z-10">
                                <tr>
                                    <th className="text-left px-2 py-1.5">Produit</th>
                                    <th className="text-right px-2 py-1.5 w-20">Prix unit.</th>
                                    {feSites.map(s => (
                                        <th key={s.id} className="text-center px-1 py-1.5 w-20">{s.name.replace('FE ', '')}</th>
                                    ))}
                                    <th className="text-right px-2 py-1.5 w-16">Qté tot.</th>
                                    <th className="text-right px-2 py-1.5 w-20">Total</th>
                                    <th className="w-8"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it, idx) => {
                                    const totQty = lineTotalQty(it);
                                    return (
                                        <tr key={it.supplier_product_id} className="border-t">
                                            <td className="px-2 py-1.5">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono text-slate-400 w-20 shrink-0">{it.ref}</span>
                                                    <span className="font-medium">{it.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-2 py-1.5 text-right">
                                                <Input type="number" step="0.01" min="0" className="h-7 w-20 text-right text-xs ml-auto"
                                                    value={it.unit_price} onFocus={(e) => e.target.select()}
                                                    onChange={(e) => setPrice(idx, e.target.value)}
                                                    data-testid={`cons-reception-price-${idx}`} />
                                            </td>
                                            {feSites.map(s => (
                                                <td key={s.id} className="px-1 py-1.5 text-center">
                                                    <Input type="number" min="0"
                                                        step={it.variable_weight ? '0.01' : '1'}
                                                        inputMode={it.variable_weight ? 'decimal' : 'numeric'}
                                                        className="h-7 w-16 text-right text-xs mx-auto"
                                                        value={it.quantities[s.id]}
                                                        onFocus={(e) => e.target.select()}
                                                        onChange={(e) => setQty(idx, s.id, e.target.value)}
                                                        data-testid={`cons-reception-qty-${idx}-${s.id}`} />
                                                </td>
                                            ))}
                                            <td className={`px-2 py-1.5 text-right font-semibold tabular-nums ${totQty > 0 ? 'text-slate-900' : 'text-orange-500'}`} data-testid={`cons-reception-totqty-${idx}`}>
                                                {Math.round(totQty * 100) / 100}
                                            </td>
                                            <td className="px-2 py-1.5 text-right tabular-nums">{formatCurrency(totQty * (parseFloat(it.unit_price) || 0))}</td>
                                            <td className="px-1 py-1.5">
                                                <button type="button" onClick={() => removeItem(idx)} className="text-slate-300 hover:text-red-500" data-testid={`cons-reception-remove-${idx}`}>
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <p className="text-sm text-slate-400 text-center py-8">
                            {supplierId ? 'Ajoutez des produits puis répartissez les quantités entre les 6 sites.' : 'Sélectionnez un fournisseur pour commencer.'}
                        </p>
                    )}
                </div>

                <div className="flex items-center justify-between border-t pt-3 flex-shrink-0">
                    <span className="text-sm text-slate-500">
                        Une réception <span className="font-mono">REC-…</span> sera créée pour chaque site ayant une quantité &gt; 0.
                    </span>
                    <div className="flex items-center gap-3">
                        <span className="font-semibold text-slate-900" data-testid="cons-reception-grand-total">{formatCurrency(grandTotal)}</span>
                        <Button onClick={handleSave} disabled={saving || !items.length}
                            className="bg-emerald-600 hover:bg-emerald-700" data-testid="cons-reception-save-btn">
                            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            Valider la réception
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
